import { useEffect, useState } from 'react';
import { Keyboard, Platform, type KeyboardEvent } from 'react-native';

/**
 * Высота клавиатуры, которую надо обойти слою поверх экрана (`Modal`, нижний лист).
 *
 * <p><b>Почему не `adjustResize` и не `KeyboardAvoidingView`.</b> Окно `Modal` на Android живёт
 * отдельно от окна активности: `softwareKeyboardLayoutMode: resize` его не сжимает, а при
 * edge-to-edge (Expo SDK 55 включает его принудительно) не сжимается и само окно активности.
 * Лист, прижатый к низу, клавиатура закрывала целиком — поле поиска друзей пропадало с экрана,
 * едва в него ставили курсор (прогон 2026-10-04, сценарий `16-friends-keyboard`). На iOS
 * `Modal` клавиатуру не обходит никогда. Поэтому отступ считается вручную, как у поля
 * сообщения в `ChatScreen`; внутри `Modal` окно не сжимается, и отступ — просто высота
 * клавиатуры.
 *
 * <p>iOS слушает `Will`-события — лист поднимается вместе с клавиатурой, а не после неё; на
 * Android приходят только `Did`. В вебе событий нет, и значение всегда 0: там клавиатуру
 * обходит сам браузер.
 */
export function useKeyboardOverlap(enabled = true): number {
  const [height, setHeight] = useState(0);

  // Слушаем всегда, а не только пока `enabled`: лист, закрытый при открытой клавиатуре,
  // пропустил бы её «hide» и при следующем открытии стоял бы на высоте старой клавиатуры.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const onShow = (e: KeyboardEvent) => setHeight(Math.max(0, Math.round(e.endCoordinates.height)));
    const onHide = () => setHeight(0);
    // `keyboardWillChangeFrame` не слушаем: он приходит и при скрытии — с прежней высотой, и
    // лист остался бы висеть над пустым местом. Смену высоты на iOS и так приносит повторный
    // `keyboardWillShow`.
    const subs = [Keyboard.addListener(showEvent, onShow), Keyboard.addListener(hideEvent, onHide)];
    return () => subs.forEach((s) => s.remove());
  }, []);

  return enabled ? height : 0;
}
